/**
 * Dock Management's up-next strip: the trailer moving into a door. Every move locks its trailer in
 * the movement namespace with its movement type, so the dock-bound ones say which moves are coming
 * in, and flux's `incoming_dock_moves` says what and to which door. The door's own lock cannot be
 * used: a door-to-door move leaves only a safety check lock there, or none when it skips the check.
 */

// The trailer moving into a door, and the task moving it
export interface IncomingDockMove {
  dock_uuid: string;
  task_uuid: string;
  trailer_uuid: string | null;
  trailer_number: string | null;
  carrier_name: string | null;
  trailer_status_name: string | null;
  shipment_reference_number: string | null;
}

// Movement types that end at a dock door, as flux's TrailerRules @dock_bound_movement_types
const DOCK_BOUND_TYPES = ['yard_to_warehouse', 'external_to_warehouse', 'warehouse_to_warehouse', 'facility_zone_to_dock'];

// The set of incoming tasks behind the movement locks; the strip refetches only when it changes
export function incomingDockLockKey(locks: any[]): string {
  const taskUuids = locks
    .filter((lock: any) =>
      lock.namespace === 'movement' && DOCK_BOUND_TYPES.includes(String(lock.operation_type)) && lock.task_uuid
    )
    .map((lock: any) => lock.task_uuid);
  return Array.from(new Set(taskUuids)).sort().join(',');
}

// The grey lock overlay, except on a backfill: an occupied door with a move coming in shows its own
// trailer plus the strip instead. An empty door keeps the overlay, with the strip over it.
export function showsLockOverlay(isLocked: boolean, hasIncoming: boolean, doorInUse: boolean): boolean {
  return isLocked && !(hasIncoming && doorInUse);
}

// Full cards: "NEXT IN · <trailer>" over "<SCAC> · <status> · <T-number>", the T-number only when
// a shipment is on the trailer
export function incomingStripLines(move: IncomingDockMove): { title: string; details: string } {
  return {
    title: `NEXT IN · ${move.trailer_number || 'Unknown trailer'}`,
    details: [move.carrier_name, move.trailer_status_name?.toUpperCase(), move.shipment_reference_number]
      .filter(Boolean)
      .join(' · ')
  };
}

// Compact cards: one line, the trailer as "<SCAC>-<trailer>" like the card's own Trailer line
export function incomingStripCompactLine(move: IncomingDockMove): string {
  const trailer = [move.carrier_name, move.trailer_number].filter(Boolean).join('-') || 'Unknown trailer';
  const rest = [move.trailer_status_name?.toUpperCase(), move.shipment_reference_number].filter(Boolean);
  return `IN ${[trailer, ...rest].join(' · ')}`;
}
